import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BaryComponentHelper } from 'app/shared/util/bary-component-helper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaryInputCountryField } from 'app/shared/components/bary-input-country/bary-input-country.field';
import { InputsModule, InputUtilitiesModule, MdbAutoCompleterDirective, MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';
import { SubscribedContainer } from 'app/shared/util/subscribed-container';
import { COUNTRIES } from '../../../../models/constants/countries.const';
import { Country } from '../../../../models/country.model';
import { CommonModule } from '@angular/common';
import { MarkAsteriskDirective } from '../../../../directives/mark-asterisk.directive';
import { TranslateDirective } from '../../../../directives/translate.directive';

@Component({
  selector: 'jhi-bary-input-country',
  templateUrl: './bary-input-country.component.html',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, InputUtilitiesModule, InputsModule, MDBBootstrapModulesPro, MarkAsteriskDirective, TranslateDirective, TranslateModule, FormsModule],
  styleUrls: ['./bary-input-country.component.scss'],
})
export class BaryInputCountryComponent extends SubscribedContainer implements OnInit {
  readonly #translateService = inject(TranslateService);

  @Input() id = '';
  @Input() label: string | undefined;
  @Input() name: string = this.id;
  @Input() disabled = false;
  @Input() initValue: string | undefined = '';
  @Input() control = new FormControl('');
  @Input() displayField: BaryInputCountryField | 'NAME' | 'KEY' = BaryInputCountryField.KEY;

  @Output() changeEvent = new EventEmitter<string>();

  @ViewChild(MdbAutoCompleterDirective, { static: true }) mdbAutoCompleter!: MdbAutoCompleterDirective;

  private _displayValue = '';
  get displayValue(): string {
    return this._displayValue;
  }

  set displayValue(value: string) {
    if (this.displayField === BaryInputCountryField.NAME && value) {
      const translate = this.#translateService.instant(COUNTRIES[value].translationKey);
      this._displayValue = translate || value;
    } else {
      this._displayValue = value;
    }
  }

  searchText = new Subject<string>();
  results: Observable<Country[]> = of([]);
  isInvalid = true;
  isInit = true;

  constructor() {
    super();
  }

  ngOnInit(): void {
    if (!this.id) {
      this.id = BaryComponentHelper.makeId();
    }
    this.displayValue = (this.control.value ? this.control.value : this.initValue) as string;
    this.subscribeChanges();
    this.results = this.searchText
      .pipe(takeUntil(this.destroyed$))
      .pipe(debounceTime(300))
      .pipe(
        startWith<string, any>(''),
        switchMap((searchText: string) => this.findData(searchText))
      );
  }

  subscribeChanges(): void {
    this.control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value !== this._displayValue) {
        if (!value && this.control.untouched && this.control.pristine) {
          // Il s'agit d'un reset, alors il faut qu'on propage
          // le reset.
          this.displayValue = '';
        } else {
          this.displayValue = this.control.value as string;
        }
      }
    });
  }

  findData(searchText: string): Observable<any> {
    if (searchText && searchText.length > 1) {
      this.isInvalid = false;
      return of(
        Object.values(COUNTRIES)
          .filter(v => this.#translateService.instant(v.translationKey).toUpperCase().trim().includes(searchText.toUpperCase().trim()))
          .sort()
      );
    } else {
      this.isInvalid = true;
      return of();
    }
  }

  getMessage(): string {
    return this.#translateService.instant(this.isInvalid ? 'components.inputs.country.requireMinChars' : 'components.inputs.country.noResult') as string;
  }

  handleSelect($event: any): void {
    this.displayValue = $event.text;
    this.mdbAutoCompleter.writeValue(this.displayValue);
    this.control.setValue($event.text);
    this.changeEvent.emit($event.text);
  }

  handleInput($event: any): void {
    if ($event) {
      this.searchText.next($event);
    }
  }

  handleClear(): void {
    this.control.setValue(null);
    this.searchText.next('');
    this.mdbAutoCompleter.clear();
    this.mdbAutoCompleter.writeValue('');
  }
}
